import { Fragment, useEffect, useMemo, useState, type ReactElement } from 'react';
import { getApplicableBrokerProducts, useBrokers } from '../hooks/useBrokers';
import { PRODUCT_LABELS, type ProductKey, type UseForAdSetting } from '../types/vehicle';
import { ModalReasonFooter } from './ModalReasonFooter';
import { VapModal } from './VapModal';

interface Props {
  vehicleLabel: string;
  dealerLocationId: number | null | undefined;
  currentSettings: UseForAdSetting[];
  isOpen: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (settings: UseForAdSetting[], reason: string) => void;
}

const dividerStyle = {
  border: 'none',
  borderTop: '1px solid #e5e5e5',
  margin: '1rem 0'
};

function cellKey(broker: string, product: ProductKey): string {
  return `${broker}__${product}`;
}

function buildInitialState(
  applicable: Array<{ broker: string; product: ProductKey }>,
  settings: UseForAdSetting[]
): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const { broker, product } of applicable) {
    state[cellKey(broker, product)] = true;
  }
  for (const setting of settings) {
    if (!setting.broker) continue;
    const k = cellKey(setting.broker, setting.product);
    // Only honour overrides that are part of the current applicable matrix.
    if (k in state) {
      state[k] = setting.useForAd;
    }
  }
  return state;
}

function statesEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

const PRODUCT_COLUMNS: readonly ProductKey[] = ['PL', 'FL', 'HK', 'OL'];

interface ProductCellProps {
  broker: string;
  product: ProductKey;
  isOffered: boolean;
  isOn: boolean;
  isSubmitting: boolean;
  onToggle: (broker: string, product: ProductKey) => void;
}

function ProductCell({ broker, product, isOffered, isOn, isSubmitting, onToggle }: ProductCellProps): ReactElement {
  if (!isOffered) {
    return (
      <div
        className="vap-advertising-matrix__cell vap-advertising-matrix__cell--na"
        aria-label={`${broker} ${product}: not offered`}
      >
        —
      </div>
    );
  }

  const handleClick = (): void => onToggle(broker, product);

  return (
    <div className="vap-advertising-matrix__cell">
      <button
        type="button"
        className={`vap-toggle ${isOn ? 'vap-toggle--on' : 'vap-toggle--off'}`}
        onClick={handleClick}
        disabled={isSubmitting}
        aria-pressed={isOn}
        aria-label={`${broker} ${product}: ${isOn ? 'On' : 'Off'}`}
      >
        {isOn ? 'On' : 'Off'}
      </button>
    </div>
  );
}

interface BrokerRowProps {
  broker: string;
  products: ProductKey[];
  state: Record<string, boolean>;
  isSubmitting: boolean;
  onToggle: (broker: string, product: ProductKey) => void;
}

function BrokerRow({ broker, products, state, isSubmitting, onToggle }: BrokerRowProps): ReactElement {
  return (
    <>
      <div className="vap-advertising-matrix__broker">{broker}</div>
      {PRODUCT_COLUMNS.map((product) => (
        <ProductCell
          key={product}
          broker={broker}
          product={product}
          isOffered={products.includes(product)}
          isOn={state[cellKey(broker, product)]}
          isSubmitting={isSubmitting}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}

export function AdvertisingModal({
  vehicleLabel,
  dealerLocationId,
  currentSettings,
  isOpen,
  isSubmitting,
  onCancel,
  onConfirm
}: Props): ReactElement | null {
  const { data: brokers, isLoading: brokersLoading, isError: brokersError } = useBrokers();

  const applicable = useMemo(
    () => (brokers ? getApplicableBrokerProducts(brokers, dealerLocationId ?? null) : []),
    [brokers, dealerLocationId]
  );

  // Group applicable cells by broker for table rendering. Preserves broker order from useBrokers.
  const brokerRows = useMemo(() => {
    const rows = new Map<string, ProductKey[]>();
    for (const { broker, product } of applicable) {
      const existing = rows.get(broker);
      if (existing) {
        existing.push(product);
      } else {
        rows.set(broker, [product]);
      }
    }
    return Array.from(rows.entries());
  }, [applicable]);

  const initialState = useMemo(() => buildInitialState(applicable, currentSettings), [applicable, currentSettings]);
  const [state, setState] = useState<Record<string, boolean>>(initialState);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setState(initialState);
      setReason('');
    }
  }, [isOpen, initialState]);

  if (!isOpen) return null;

  const toggle = (broker: string, product: ProductKey): void => {
    setState((prev) => ({ ...prev, [cellKey(broker, product)]: !prev[cellKey(broker, product)] }));
  };

  const changed = !statesEqual(state, initialState);

  const handleConfirm = (): void => {
    // Send only blocked combinations — backend treats missing entries as advertised=true.
    const settings: UseForAdSetting[] = [];
    for (const { broker, product } of applicable) {
      if (!state[cellKey(broker, product)]) {
        settings.push({ broker, product, useForAd: false });
      }
    }
    onConfirm(settings, reason.trim());
  };

  const renderBody = (): ReactElement => {
    if (brokersLoading) {
      return <p style={{ margin: 0, color: '#666' }}>Loading brokers…</p>;
    }
    if (brokersError) {
      return (
        <p style={{ margin: 0, color: '#a00' }}>
          Failed to load brokers. Close and retry, or contact platform team if the issue persists.
        </p>
      );
    }
    if (brokerRows.length === 0) {
      return (
        <p style={{ margin: 0, color: '#666' }}>
          No brokers cover this vehicle{dealerLocationId == null ? ' (dealer location is missing)' : ''}. There is
          nothing to advertise.
        </p>
      );
    }
    return (
      <div className="vap-advertising-matrix">
        <div className="vap-advertising-matrix__head vap-advertising-matrix__head--broker">Broker</div>
        {PRODUCT_COLUMNS.map((product) => (
          <div key={product} className="vap-advertising-matrix__head" title={PRODUCT_LABELS[product]}>
            {product}
          </div>
        ))}

        {brokerRows.map(([broker, products]) => (
          <Fragment key={broker}>
            <BrokerRow
              broker={broker}
              products={products}
              state={state}
              isSubmitting={isSubmitting}
              onToggle={toggle}
            />
          </Fragment>
        ))}
      </div>
    );
  };

  const canConfirm = changed && brokerRows.length > 0;

  return (
    <VapModal id="vap-advertising-modal" title={`Advertising — ${vehicleLabel}`} onClose={onCancel}>
      <div style={{ display: 'flex', flexDirection: 'column', padding: '0.5rem 0.25rem' }}>
        <p style={{ margin: '0 0 1rem 0', color: '#444' }}>
          Toggle whether this vehicle is advertised for each product on each broker. <strong>On</strong> means the
          vehicle is shown to consumers; <strong>Off</strong> blocks advertising for that combination.
        </p>

        {renderBody()}

        <hr style={dividerStyle} />

        <ModalReasonFooter
          reason={reason}
          onReasonChange={setReason}
          onCancel={onCancel}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting}
          confirmLabel="Apply"
          confirmDisabled={!canConfirm}
        />
      </div>
    </VapModal>
  );
}
